/**
 * ndt_compliance — NDT standing in five areas (GET /dashboard/ndt-compliance,
 * api#1142): technician certs, welder quals, instrument calibration, sources
 * (leak tests and custody) and dose. Counts only — no units, no money.
 *
 * The API counts everything with the same status functions the NDT screens
 * use; nothing here recomputes a status or a total.
 *
 * THREE KINDS OF NOTHING, NEVER ONE SPELLING:
 *   - hasData:false — the company holds no NDT records at all. One short line,
 *     no figures: a grid of "—" would read as a broken read, and a grid of 0
 *     as a clean register.
 *   - an area that is null — no rows in THAT area. Its figures read "—" and
 *     the area says "None on file". Never 0.
 *   - doseRestricted — this role may not see every worker's dose. The dose
 *     area says "Restricted", not "—" and not 0: the records may well exist.
 *   And dose.overLevel null means no investigation level is configured — "—"
 *   with the reason, never a guessed 0.
 *
 * Each area links to its NDT page, filtered by ?status= where that page
 * reads one (technicians, instruments) to the worst status present.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { api } from '../../../api/client';
import { unwrap } from '../../../lib/api-envelope';
import { formatCalendarDate } from '../../../lib/calendar-date';
import { LOAD_FAILURE_QUERY_OPTIONS, queryLoadFailure } from '../../../lib/load-failure';
import { displayValue } from '../../../features/fieldiq-handoff/contracts';
import type { NdtCompliance } from '../../../types/ndt-inspection';
import { LoadFailureNotice } from '../../ui/LoadFailureNotice';
import { WidgetFigure, WidgetPanel, WidgetPending } from '../WidgetPanel';

export const useNdtCompliance = () =>
  useQuery({
    queryKey: ['dashboard-ndt-compliance'],
    queryFn: () => api.dashboard.ndtCompliance().then((r) => unwrap<NdtCompliance>(r.data)),
    refetchInterval: 30_000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });

const n = (v: number | null | undefined) => displayValue(v ?? null);
const over = (v: number | null | undefined) => typeof v === 'number' && v > 0;

/** The page link, filtered to the worst status present when the page reads ?status=. */
const filtered = (base: string, worst: [string, number | null | undefined][]) => {
  const hit = worst.find(([, c]) => over(c));
  return hit ? `${base}?status=${hit[0]}` : base;
};

function Area({ testId, title, to, note, children }: {
  testId: string;
  title: string;
  to: string;
  /** Why the figures are absent, said once for the area. */
  note?: string;
  children: ReactNode;
}) {
  return (
    <div data-testid={testId} className="min-w-0 py-4 xl:px-4 xl:first:pl-0">
      <Link
        to={to}
        data-testid={`${testId}-link`}
        className="inline-flex items-center gap-1 text-sm font-extrabold text-fieldiq-text underline decoration-fi-amber decoration-2 underline-offset-4"
      >
        {title} <ChevronRight className="size-3.5" aria-hidden="true" />
      </Link>
      {note && <p data-testid={`${testId}-note`} className="mt-1 text-xs text-fieldiq-muted">{note}</p>}
      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-4">{children}</div>
    </div>
  );
}

export default function NdtComplianceWidget() {
  const q = useNdtCompliance();
  const failure = q.data === undefined ? queryLoadFailure(q) : null;
  const d = q.data;

  let body: ReactNode;
  if (failure) {
    body = <LoadFailureNotice error={null} kind={failure} subject="NDT compliance" className="p-4" />;
  } else if (d === undefined) {
    body = <WidgetPending rows={2} />;
  } else if (!d || !d.hasData) {
    body = (
      <p data-testid="ndt-compliance-empty" className="px-4 py-6 text-center text-sm text-fieldiq-muted">
        No NDT records on file yet. Technician certs, welder quals, instruments, sources and dose show here once they are.
      </p>
    );
  } else {
    const { certs, welderQuals: quals, instruments: inst, sources: src, dose } = d;
    const none = 'None on file';
    body = (
      <div className="grid grid-cols-1 divide-y divide-fieldiq-border px-4 sm:grid-cols-2 sm:divide-y-0 xl:grid-cols-5 xl:divide-x">
        <Area
          testId="ndt-area-certs"
          title="Tech certs"
          to={filtered('/ndt-inspection/technicians', [['expired', certs?.expired], ['due_soon', certs?.dueSoon]])}
          note={certs ? undefined : none}
        >
          <WidgetFigure testId="ndt-certs-expired" label="Expired" alert={over(certs?.expired)} value={n(certs?.expired)} />
          <WidgetFigure testId="ndt-certs-due-soon" label="Due soon" value={n(certs?.dueSoon)} />
          <WidgetFigure testId="ndt-certs-valid" label="Valid" value={n(certs?.valid)} />
          <WidgetFigure testId="ndt-certs-none" label="No expiry" value={n(certs?.none)} />
        </Area>
        <Area testId="ndt-area-quals" title="Welder quals" to="/ndt-inspection/welders" note={quals ? undefined : none}>
          <WidgetFigure testId="ndt-quals-expired" label="Expired" alert={over(quals?.expired)} value={n(quals?.expired)} />
          <WidgetFigure testId="ndt-quals-lapsed" label="Lapsed" alert={over(quals?.lapsed)} value={n(quals?.lapsed)} />
          <WidgetFigure testId="ndt-quals-due-soon" label="Due soon" value={n(quals?.dueSoon)} />
          <WidgetFigure testId="ndt-quals-valid" label="Valid" value={n(quals?.valid)} />
          <WidgetFigure testId="ndt-quals-none" label="No expiry" value={n(quals?.none)} />
        </Area>
        <Area
          testId="ndt-area-instruments"
          title="Instruments"
          to={filtered('/ndt-inspection/instruments', [['overdue', inst?.overdue], ['never', inst?.never], ['due_soon', inst?.dueSoon]])}
          note={inst ? undefined : none}
        >
          <WidgetFigure testId="ndt-inst-overdue" label="Overdue" alert={over(inst?.overdue)} value={n(inst?.overdue)} />
          <WidgetFigure testId="ndt-inst-never" label="Never calibrated" alert={over(inst?.never)} value={n(inst?.never)} />
          <WidgetFigure testId="ndt-inst-due-soon" label="Due soon" value={n(inst?.dueSoon)} />
          <WidgetFigure testId="ndt-inst-valid" label="Valid" value={n(inst?.valid)} />
        </Area>
        <Area testId="ndt-area-sources" title="Sources" to="/ndt-inspection/sources" note={src ? undefined : none}>
          <WidgetFigure testId="ndt-src-leak-overdue" label="Leak test overdue or failed" alert={over(src?.leakOverdue)} value={n(src?.leakOverdue)} />
          <WidgetFigure testId="ndt-src-leak-due" label="Leak test due" value={n(src?.leakDueSoon)} />
          <WidgetFigure
            testId="ndt-src-checked-out"
            label="Checked out"
            alert={over(src?.checkedOutLong)}
            value={n(src?.checkedOut)}
            sub={src && src.checkedOutLong > 0
              ? `${src.checkedOutLong} out over ${src.checkedOutAlertHours} h`
              : undefined}
          />
          <WidgetFigure testId="ndt-src-held" label="Held" value={n(src?.held)} sub={src ? `of ${src.total}` : undefined} />
        </Area>
        <Area
          testId="ndt-area-dose"
          title="Dose"
          to="/ndt-inspection/dosimetry"
          note={d.doseRestricted ? 'Restricted — your role sees only your own dose records' : dose ? undefined : none}
        >
          {d.doseRestricted ? (
            <WidgetFigure testId="ndt-dose-over" label="Over level" value="Restricted" />
          ) : (
            <>
              <WidgetFigure
                testId="ndt-dose-over"
                label="Over level"
                alert={over(dose?.overLevel)}
                value={n(dose?.overLevel)}
                sub={dose && dose.overLevel === null ? 'No investigation level set' : undefined}
              />
              <WidgetFigure testId="ndt-dose-workers" label="Workers" value={n(dose?.workers)} />
              <WidgetFigure
                testId="ndt-dose-latest"
                label="Latest period"
                value={formatCalendarDate(dose?.latestPeriodEnd ?? null, { month: 'short', day: 'numeric' }) ?? '—'}
              />
            </>
          )}
        </Area>
      </div>
    );
  }

  return (
    <WidgetPanel widgetKey="ndt_compliance" eyebrow="Inspection records" title="NDT Compliance">
      {body}
    </WidgetPanel>
  );
}
